import { Trans } from '@lingui/react/macro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  GetScreenerResponseDto,
  ProjectDetailsResponseDto,
} from '@/api/core-api/types.gen';
import type { ScreenerAdvanceParams } from '@/routes/participants/_authenticated/projects/$projectId/-components/screener-question-stepper';

import { LoadingIndicator } from '@/components/base/loading-indicator/loading-indicator';
import { PreSubmissionNda } from '@/routes/participants/_authenticated/projects/$projectId/-components/pre-submission-nda';
import { ScreenerQuestionStepper } from '@/routes/participants/_authenticated/projects/$projectId/-components/screener-question-stepper';
import { formatResponsesForApi } from '@/routes/participants/_authenticated/projects/$projectId/-hooks/use-screener-submission';
import { admittedRouteForKind } from '@/routes/participants/_public/invite/-invite/admitted-route';
import {
  externalScreenerQueryOptions,
  saveExternalScreenerAnswers,
} from '@/routes/participants/_public/invite/-invite/queries';
import { publicProjectDetailsQueryOptions } from '@/routes/participants/_public/projects/$projectId/-public-apply/queries';

/** Statuses at which the participant has been let into the interview. */
const ADMITTED_STATUSES = new Set(['INVITED', 'SCHEDULED', 'ATTENDED']);

/**
 * The screener for an external participant, after the invite link's landing
 * page created their account and filed their application as a draft.
 *
 * Each answer is saved as it is given, the same way the participant app's
 * screener does, so leaving and coming back resumes where they stopped. The
 * save that completes the screener (or the agreement, when the project has
 * one) files the application: a qualifying participant is admitted and goes
 * straight into the interview; anyone else sees one neutral confirmation.
 */
export const Route = createFileRoute(
  '/participants/_external/external/projects/$projectId/screener',
)({
  loader: async ({ params, context }) => {
    const userId = context.externalUserId;
    const [screener, details] = await Promise.all([
      context.queryClient.ensureQueryData(
        externalScreenerQueryOptions({ projectId: params.projectId, userId }),
      ),
      context.queryClient.ensureQueryData(
        publicProjectDetailsQueryOptions({ projectId: params.projectId }),
      ),
    ]);

    if (isAdmitted(screener)) {
      throw redirect({
        to: admittedRouteForKind(details.project.kindOfResearch),
        params: { projectId: params.projectId },
      });
    }
  },
  errorComponent: Unavailable,
  component: ExternalScreenerRoute,
});

function isAdmitted(screener: GetScreenerResponseDto): boolean {
  const status = screener.existingResponse?.status;
  return status !== undefined && ADMITTED_STATUSES.has(status);
}

function ExternalScreenerRoute() {
  const { projectId } = Route.useParams();
  const { externalUserId } = Route.useRouteContext();
  const { data: screener } = useQuery(
    externalScreenerQueryOptions({ projectId, userId: externalUserId }),
  );
  const { data: details } = useQuery(
    publicProjectDetailsQueryOptions({ projectId }),
  );

  if (!screener || !details) {
    return <LoadingIndicator />;
  }

  return (
    <PageContainer>
      <ExternalScreener
        projectId={projectId}
        userId={externalUserId}
        screener={screener}
        project={details.project}
      />
    </PageContainer>
  );
}

/** The participant app's apply page container, so the screener matches it. */
function PageContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-[var(--max-width-container)] px-4 pt-6 pb-18 md:px-8">
      {children}
    </div>
  );
}

type Stage = 'questions' | 'nda' | 'file' | 'received' | 'stale';

function initialStage(screener: GetScreenerResponseDto): Stage {
  const existing = screener.existingResponse;
  if (existing && existing.status !== 'DRAFT') {
    return 'received';
  }
  if (
    existing?.outstandingSteps?.nda ||
    screener.isScreenerComplete ||
    // A skip-screener link: only the agreement, if any, stands between the
    // participant and the interview.
    screener.skipScreenerQuestions
  ) {
    // Nothing left to answer and no agreement to accept: the draft still
    // has to be filed, which an empty save does.
    return screener.nda?.requiresNda ? 'nda' : 'file';
  }
  return 'questions';
}

function ExternalScreener({
  projectId,
  userId,
  screener,
  project,
}: {
  projectId: string;
  userId: string;
  screener: GetScreenerResponseDto;
  project: ProjectDetailsResponseDto['project'];
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<Stage>(() => initialStage(screener));
  const [isSaving, setIsSaving] = useState(false);
  const [ndaError, setNdaError] = useState<Error | null>(null);
  // Seconds since the last save, accumulated server-side like the
  // participant app's screener timer.
  const lastSaveAt = useRef(Date.now());

  const elapsedSinceLastSave = useCallback(() => {
    const now = Date.now();
    const seconds = Math.floor((now - lastSaveAt.current) / 1000);
    lastSaveAt.current = now;
    return seconds;
  }, []);

  /** Where a filed application goes next. */
  const settle = useCallback(
    (status: string) => {
      void queryClient.invalidateQueries({
        queryKey: externalScreenerQueryOptions({ projectId, userId }).queryKey,
      });
      if (ADMITTED_STATUSES.has(status)) {
        void navigate({
          to: admittedRouteForKind(project.kindOfResearch),
          params: { projectId },
        });
        return;
      }
      setStage(status === 'DRAFT' ? 'nda' : 'received');
    },
    [navigate, project.kindOfResearch, projectId, queryClient, userId],
  );

  const advance = useCallback(
    async ({ currentResponse, questions }: ScreenerAdvanceParams) => {
      setIsSaving(true);
      try {
        return await saveExternalScreenerAnswers({
          projectId,
          responses: formatResponsesForApi([currentResponse], questions),
          totalTime: elapsedSinceLastSave(),
        });
      } finally {
        setIsSaving(false);
      }
    },
    [elapsedSinceLastSave, projectId],
  );

  const questions = screener.questions ?? [];

  if (stage === 'file') {
    return (
      <FileOnArrival
        projectId={projectId}
        totalTime={elapsedSinceLastSave}
        onFiled={settle}
        onFailed={() => setStage('stale')}
      />
    );
  }

  if (stage === 'received') {
    return <Received />;
  }

  if (stage === 'stale') {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <h1 className="text-display-xs font-semibold text-primary">
          <Trans>This study has changed</Trans>
        </h1>
        <p className="mt-3 text-md text-tertiary">
          <Trans>Please reload the page to continue.</Trans>
        </p>
      </div>
    );
  }

  if (stage === 'nda' && screener.nda) {
    return (
      <div className="mx-auto max-w-2xl">
        <PreSubmissionNda
          nda={screener.nda}
          isSubmitting={isSaving}
          error={ndaError}
          onAgree={async () => {
            setIsSaving(true);
            setNdaError(null);
            try {
              const filed = await saveExternalScreenerAnswers({
                projectId,
                responses: [],
                totalTime: elapsedSinceLastSave(),
                agree: true,
              });
              settle(filed.status);
            } catch (error) {
              setNdaError(
                error instanceof Error ? error : new Error(String(error)),
              );
            } finally {
              setIsSaving(false);
            }
          }}
        />
      </div>
    );
  }

  return (
    <ScreenerQuestionStepper
      projectId={projectId}
      project={project}
      questions={questions}
      initialQuestionUid={screener.nextQuestionUid ?? undefined}
      initialIsNextQuestionLast={screener.isNextQuestionLast}
      questionOffset={screener.existingResponse?.answeredQuestionIds?.length}
      totalQuestions={questions.length}
      advance={advance}
      onComplete={(_responses, result) => settle(result.status)}
      onError={() => setStage('stale')}
      shuffleSeed={userId}
      isSubmitting={isSaving}
    />
  );
}

/**
 * Files a draft with nothing left to answer (a project without questions or
 * agreement, or one whose last save was interrupted) once, on arrival.
 */
function FileOnArrival({
  projectId,
  totalTime,
  onFiled,
  onFailed,
}: {
  projectId: string;
  totalTime: () => number;
  onFiled: (status: string) => void;
  onFailed: () => void;
}) {
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void saveExternalScreenerAnswers({
      projectId,
      responses: [],
      totalTime: totalTime(),
    })
      .then((filed) => onFiled(filed.status))
      .catch(() => onFailed());
  }, [onFailed, onFiled, projectId, totalTime]);

  return <LoadingIndicator />;
}

/**
 * One neutral outcome for every filed application that was not admitted
 * (disqualified, study full, quota-held): nothing here may confirm a
 * negative.
 */
function Received() {
  return (
    <div
      className="mx-auto max-w-lg py-16 text-center"
      data-testid="external-screener-received"
    >
      <h1 className="text-display-xs font-semibold text-primary">
        <Trans>Thanks, we&apos;ve received your answers</Trans>
      </h1>
      <p className="mt-3 text-md text-tertiary">
        <Trans>The researcher will be in touch. You can close this page.</Trans>
      </p>
    </div>
  );
}

function Unavailable() {
  return (
    <div className="mx-auto max-w-lg px-4 py-16 text-center">
      <h1 className="text-display-xs font-semibold text-primary">
        <Trans>This study is not available</Trans>
      </h1>
      <p className="mt-3 text-md text-tertiary">
        <Trans>
          Check the link you were sent, or ask the researcher for a new one.
        </Trans>
      </p>
    </div>
  );
}
